import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { getUsuariosParaTransferir } from "@/features/user/userThunks";
// Placeholder image for users without profile picture
import PLACEHOLDER_IMAGE from "@/assets/avatars/avatar-generic.webp";


export default function UsuariosParaTransferir({
  closeTransferir,
  onTransferirClick,
}) {
  const dispatch = useDispatch();
  
  // Estados
  const [usuarios, setUsuarios] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingUsuarios, setIsLoadingUsuarios] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [buscarPor, setBuscarPor] = useState("todas");
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  
  // Ref para el contenedor de scroll
  const scrollContainerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Función para cargar usuarios
  const loadUsuarios = async (page, reset = false) => {
    if ((isLoadingUsuarios || !hasMore) && !reset) return;

    setIsLoadingUsuarios(true);
    try {
      const response = await dispatch(
        getUsuariosParaTransferir({
          buscarPor,
          busqueda,
          page,
          limit: 9,
          datos: "uid,imgPerfil,nombre,apellido,alias",
        })
      ).unwrap();

      if (reset) {
        setUsuarios(response.usuarios);
        setCurrentPage(1);
      } else {
        setUsuarios((prev) => [...prev, ...response.usuarios]);
      }

      setHasMore(response.pagination.hasNextPage);
      setCurrentPage(response.pagination.currentPage);
    } catch (error) {
      console.error("Error al cargar usuarios:", error);
    } finally {
      setIsLoadingUsuarios(false);
    }
  };

  // Cargar usuarios iniciales
  useEffect(() => {
    loadUsuarios(1, true);
  }, []);

  // Efecto para búsqueda con debounce
  useEffect(() => {
    // Limpiar el timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Configurar nuevo timer
    debounceTimerRef.current = setTimeout(() => {
      setUsuarioSeleccionado(null);
      loadUsuarios(1, true);
    }, 500);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [busqueda, buscarPor]);

  // Manejador de scroll infinito
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Si está cerca del final (80%), cargar más
      if (scrollPercentage >= 0.8 && hasMore && !isLoadingUsuarios) {
        loadUsuarios(currentPage + 1);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [hasMore, isLoadingUsuarios, currentPage]);

  const handleSelectUser = (usuario) => {
    if (usuarioSeleccionado?.uid === usuario.uid) {
      setUsuarioSeleccionado(null);
    } else {
      setUsuarioSeleccionado(usuario);
    }
  };

  const handleTransferir = () => {
    if (usuarioSeleccionado && onTransferirClick) {
      onTransferirClick(usuarioSeleccionado);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto flex flex-col items-center h-[77.25dvh] bg-aw-bluedark border-2 border-b-0 border-aw-green rounded-t-4xl pt-6 pb-4 px-2">
      <h2 className="text-2xl font-semibold text-white mb-4 text-center">
        Transferir el Ticket
      </h2>

      {/* Select de tipo de búsqueda y input de búsqueda */}
      <div className="w-full mb-6 flex flex-col items-center justify-center gap-3 px-4">
        <div className="w-full max-w-xs">
          <select
            value={buscarPor}
            onChange={(e) => setBuscarPor(e.target.value)}
            className="w-full rounded-xl border border-gray-500 py-2 px-4 text-center bg-aw-bluedark text-white text-base focus:outline-none focus:ring-1 focus:ring-aw-green"
          >
            <option value="todas">Buscar por: Todas</option>
            <option value="nombre">Buscar por: Nombre</option>
            <option value="apellido">Buscar por: Apellido</option>
            <option value="alias">Buscar por: Alias</option>
          </select>
        </div>
        <div className="relative w-full max-w-xs">
          <input
            type="text"
            placeholder="Buscar usuario para Transferir Ticket"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full rounded-xl border border-gray-500 py-2 pl-4 pr-10 text-center text-white text-base focus:outline-none focus:ring-1 focus:ring-aw-green"
          />
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white text-lg"
          />
        </div>
      </div>

      {/* Grid de usuarios con scroll */}
      <div
        ref={scrollContainerRef}
        className="grid grid-cols-3 gap-y-6 gap-x-2 w-full px-4 pt-4 mb-6 overflow-auto scrollbar-box thin h-[40dvh] md:h-[45dvh] lg:h-[50dvh]"
      >
        {usuarios.map((usuario, i) => {
          const isSelected = usuarioSeleccionado?.uid === usuario.uid;
          return (
            <div
              key={usuario.uid || i}
              className={`flex flex-col items-center transition-all duration-200 ${
                isSelected ? "scale-105" : ""
              }`}
              onClick={() => handleSelectUser(usuario)}
            >
              <div
                className={`w-20 h-20 rounded-full border-4 flex items-center justify-center overflow-hidden mb-1 bg-white cursor-pointer transition-all duration-200 ${
                  isSelected
                    ? "border-aw-green"
                    : "border-gray-300 bg-gray-300"
                }`}
              >
                <img
                  src={usuario.imgPerfil || PLACEHOLDER_IMAGE}
                  alt={`${usuario.nombre} ${usuario.apellido}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = PLACEHOLDER_IMAGE;
                  }}
                />
              </div>
              <div
                className={`text-xs font-semibold truncate w-20 text-center ${
                  isSelected ? "text-gray-200" : "text-gray-200"
                }`}
              >
                {usuario.nombre}
              </div>
              {usuario.alias && (
                <div
                  className={`text-[10px] font-semibold ${
                    isSelected
                      ? "text-aw-green bg-aw-gray rounded-2xl px-1.5 py-0.5"
                      : "text-gray-400"
                  }`}
                >
                  ({usuario.alias})
                </div>
              )}
            </div>
          );
        })}

        {/* Loading indicator */}
        {isLoadingUsuarios && (
          <div className="col-span-3 flex justify-center items-center py-4">
            <FontAwesomeIcon
              icon={faSpinner}
              className="text-aw-bluedark text-2xl animate-spin"
            />
          </div>
        )}

        {/* Mensaje si no hay usuarios */}
        {!isLoadingUsuarios && usuarios.length === 0 && (
          <div className="col-span-3 flex justify-center items-center py-8 text-aw-bluedark-50">
            No se encontraron usuarios
          </div>
        )}
      </div>

      {/* Botón cancelar/transferir */}
      {!usuarioSeleccionado ? (
        <button
          onClick={closeTransferir}
          className="w-full max-w-xs bg-aw-bluegray text-gray-200 text-lg font-semibold rounded-xl py-3 mt-auto tracking-widest cursor-pointer hover:scale-95 duration-300"
        >
          CANCELAR
        </button>
      ) : (
        <button
          onClick={handleTransferir}
          className="w-full max-w-xs bg-aw-green text-aw-bluedark text-lg font-semibold rounded-xl py-3 mt-auto tracking-widest cursor-pointer hover:scale-95 duration-300"
        >
          TRANSFERIR
        </button>
      )}
    </div>
  );
}

